import { NgModule } from '@angular/core';
import { getWebAutoInstrumentations } from '@opentelemetry/auto-instrumentations-web';
import { ZoneContextManager } from '@opentelemetry/context-zone';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { resourceFromAttributes } from '@opentelemetry/resources';
import { BatchSpanProcessor, ParentBasedSampler, TraceIdRatioBasedSampler } from '@opentelemetry/sdk-trace-base';
import { WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import { ATTR_SERVICE_NAME, ATTR_SERVICE_VERSION } from '@opentelemetry/semantic-conventions';

import { TokenService } from '@ninety/platform/services/services/token.service';
import { environment } from '@ninety/ui/web/environments';

import { LoggingService } from './logging.service';
import { OT_RESOURCE, OT_TRACER_PROVIDER } from './open-telemetry.tokens';
import { SpanService } from './span.service';

const IGNORE_URLS: RegExp[] = [
  /.*aptrinsic.com.*/,
  /.*facebook.com.*/,
  /.*fullstory.com.*/,
  /.*google-analytics.com.*/,
  /.*google.com.*/,
  /.*launchdarkly.com.*/,
  /.*linkedin.com.*/,
  /.*logs\.browser-intake-datadoghq\.com.*/,
  /.*rum\.browser-intake-datadoghq\.com.*/,
  /.*pndsn.com.*/,
  /.*profitwell.com.*/,
  /.*segment.com.*/,
  /.*segment.io.*/,
  /.*onesignal\.com.*/, //Added to ignore OneSignal calls
  /.*partnerlinks\.io.*/,
  // Direct-to-S3 uploads (attachment/avatar/print presigned PUTs). We don't trace these, and a
  // propagated `traceparent` header forces the bucket's CORS preflight to allow-list it or the
  // upload fails with a masked `status 0`. `propagateTraceHeaderCorsUrls` below is `/.+/g`, whose
  // `/g` flag makes `.test()` stateful — so the header leaks onto S3 PUTs only intermittently,
  // which matches the flaky upload failures. Ignoring the host here stops it entirely. See DEV-26988.
  /.*\.amazonaws\.com.*/,
  /.*zapier\.com.*/, // api.zapier.com's CORS preflight rejects the injected traceparent header
];

@NgModule({
  providers: [
    {
      provide: OT_RESOURCE,
      useFactory: (tokenService: TokenService) => {
        const decodedToken = tokenService.getAccessTokenDecoded();
        return resourceFromAttributes({
          [ATTR_SERVICE_NAME]: environment.service,
          [ATTR_SERVICE_VERSION]: environment.appVersion,
          ['host.name']: window.location.hostname,
          env: environment.env,
          'client.personId': decodedToken?.personId || '',
          'client.userId': decodedToken?.userId || '',
          'client.companyId': decodedToken?.companyId || '',
        });
      },
      deps: [TokenService],
    },
    {
      provide: OT_TRACER_PROVIDER,
      useFactory: resource => {
        // Configure sampling strategy based on environment
        const getSamplingRatio = () => {
          switch (environment.env) {
            case 'prod':
              return 0.75; // 75% sampling in production. May reduce to 0.5 or lower based on performance
            case 'staging':
              return 1; // 100% sampling in staging
            default:
              return 1; // 100% sampling in development/other environments
          }
        };

        let provider: WebTracerProvider;
        const sampler = new ParentBasedSampler({
          root: new TraceIdRatioBasedSampler(getSamplingRatio()),
        });

        const env = resource.attributes['env'] || environment.env;
        if (env === 'prod' || env === 'staging' || (env === 'dev' && window.location.hostname !== 'localhost')) {
          // No `headers` so the exporter uses navigator.sendBeacon: survives unload, no 10s abort,
          // and a failure is not an XHR error log (DEV-28541). Beacon payloads must stay under ~64 KB.
          const exporter = new OTLPTraceExporter({
            url: environment.openTelemetryCollectorOrigin + '/v1/traces',
          });

          provider = new WebTracerProvider({
            resource,
            sampler,
            spanProcessors: [
              new BatchSpanProcessor(exporter, {
                maxQueueSize: 2048,
                scheduledDelayMillis: 10000,
                maxExportBatchSize: 32, // keeps one beacon under the ~64 KB quota
              }),
            ],
          });

          // Register the provider with ZoneContextManager for better async support
          provider.register({
            contextManager: new ZoneContextManager(),
          });
        } else {
          // For localhost, use simple span processor for immediate console output
          provider = new WebTracerProvider({
            resource,
            sampler,
            spanProcessors: [
              // // Uncomment for console logging in development
              // new BatchSpanProcessor(new ConsoleSpanExporter(), {
              //   maxQueueSize: 1,
              //   scheduledDelayMillis: 1000,
              //   maxExportBatchSize: 256,
              // })
            ],
          });
        }

        // Register auto-instrumentations with configuration
        registerInstrumentations({
          instrumentations: [
            getWebAutoInstrumentations({
              // Configure document load instrumentation
              '@opentelemetry/instrumentation-document-load': {
                enabled: true,
              },

              // Configure user interaction instrumentation
              // Turning off user interaction instrumentation for now but can be enabled if desired
              // This instrumentation can generate a lot of spans, so use with caution
              '@opentelemetry/instrumentation-user-interaction': {
                enabled: false,
                eventNames: ['click'], //, 'submit', 'change', 'input', 'select', 'drag', 'drop'],
              },

              // Configure fetch instrumentation
              '@opentelemetry/instrumentation-fetch': {
                enabled: true,
                clearTimingResources: true,
                propagateTraceHeaderCorsUrls: [
                  /.+/g, // Propagate to all URLs - adjust as needed
                ],
                ignoreUrls: IGNORE_URLS,
              },
              // Configure XMLHttpRequest instrumentation
              '@opentelemetry/instrumentation-xml-http-request': {
                enabled: true,
                clearTimingResources: true,
                propagateTraceHeaderCorsUrls: [
                  /.+/g, // Propagate to all URLs - adjust as needed
                ],
                ignoreUrls: IGNORE_URLS,
              },
            }),
          ],
        });

        return provider;
      },
      deps: [OT_RESOURCE],
    },
    LoggingService,
    SpanService,
  ],
})
export class OpenTelemetryModule {}
